/**
 * Admin CRUD for counter-staff portal users (staff_users collection).
 * Distinct from Admin Team at /admin/staff.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Users, Plus, Search, Loader2, KeyRound, UserX, X, CheckCircle, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/staff-users`;
const BRANCHES_API = `${BACKEND_URL}/api/admin/branches`;

const ROLE_TONE = {
  manager: { bg: 'bg-[#1B375B]', text: 'text-white', label: 'Manager' },
  counter: { bg: 'bg-sky-100', text: 'text-sky-900', label: 'Counter' },
  callcenter: { bg: 'bg-emerald-100', text: 'text-emerald-900', label: 'Call Center' },
};

function RoleBadge({ role }) {
  const t = ROLE_TONE[role] || ROLE_TONE.counter;
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold ${t.bg} ${t.text}`}>
      {t.label}
    </span>
  );
}

function StaffUserModal({ open, branches, onClose, onSaved }) {
  const [form, setForm] = useState({
    email: '', full_name: '', role: 'counter', branch_id: '', password: '', phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (open) {
      setErr(null);
      setForm({
        email: '', full_name: '', role: 'counter',
        branch_id: branches[0]?.id || '', password: '', phone: '',
      });
    }
  }, [open, branches]);

  if (!open) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      await axios.post(API, form, { withCredentials: true });
      onSaved();
    } catch (e) {
      setErr(typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" data-testid="staff-user-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-5">
        <h3 className="text-lg font-bold text-[#1B375B] mb-4">Add staff user</h3>
        <div className="space-y-3">
          <input placeholder="Email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" data-testid="staff-user-email" />
          <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" />
          <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm">
            {Object.keys(ROLE_TONE).map((r) => <option key={r} value={r}>{ROLE_TONE[r].label}</option>)}
          </select>
          <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm">
            <option value="">— Select branch —</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name} ({b.city})</option>)}
          </select>
          <input placeholder="Initial password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm font-mono" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 border rounded-lg text-sm" />
          {err && <p className="text-sm text-rose-700">{err}</p>}
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy || !form.email || !form.full_name || !form.branch_id || !form.password} className="px-4 py-2 bg-[#1B375B] text-white text-sm font-semibold rounded-lg disabled:opacity-50 inline-flex items-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Create
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffUserManagement() {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [me, setMe] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [toast, setToast] = useState(null);

  const isSuperAdmin = me?.role === 'super_admin';

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const [staff, branchRes, meRes] = await Promise.all([
        axios.get(API, { withCredentials: true }),
        axios.get(BRANCHES_API, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/me`, { withCredentials: true }),
      ]);
      setRows(staff.data.rows || []);
      setBranches((branchRes.data.rows || []).filter((b) => b.is_active !== false));
      setMe(meRes.data);
    } catch (e) {
      setToast({ kind: 'err', text: 'Failed to load staff users' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      (r.email || '').toLowerCase().includes(q)
      || (r.full_name || '').toLowerCase().includes(q));
  }, [rows, search]);

  const deactivate = async (id) => {
    if (!window.confirm('Deactivate this staff user?')) return;
    try {
      await axios.put(`${API}/${id}/deactivate`, {}, { withCredentials: true });
      setToast({ kind: 'ok', text: 'Staff user deactivated' });
      fetchRows();
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Failed' });
    }
  };

  const resetPassword = async (id) => {
    const pw = window.prompt('New password (min 8 chars):');
    if (!pw || pw.length < 8) return;
    try {
      await axios.post(`${API}/${id}/reset-password`, { new_password: pw }, { withCredentials: true });
      setToast({ kind: 'ok', text: 'Password reset' });
    } catch (e) {
      setToast({ kind: 'err', text: e?.response?.data?.detail || 'Failed' });
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="staff-user-management">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Users className="w-8 h-8 text-[#1B375B]" />
          <div>
            <h1 className="text-2xl font-bold text-[#1B375B]">Staff Users</h1>
            <p className="text-sm text-gray-500">Counter portal accounts (separate from Admin Team)</p>
          </div>
        </div>
        {isSuperAdmin && (
          <button onClick={() => setModalOpen(true)} className="inline-flex items-center gap-2 px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm font-semibold" data-testid="staff-user-add-btn">
            <Plus className="w-4 h-4" /> Add staff user
          </button>
        )}
      </div>

      {toast && (
        <div className={`mb-4 p-3 rounded-lg border text-sm ${toast.kind === 'ok' ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
          {toast.text}
          <button onClick={() => setToast(null)} className="float-right"><X className="w-4 h-4" /></button>
        </div>
      )}

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email…" className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm" />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#659A9B]" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-[10px] uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Status</th>
                {isSuperAdmin && <th className="px-4 py-3">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-gray-100">
                  <td className="px-4 py-3 font-semibold">{r.full_name}</td>
                  <td className="px-4 py-3 text-gray-600">{r.email}</td>
                  <td className="px-4 py-3"><RoleBadge role={r.role} /></td>
                  <td className="px-4 py-3">{branchMap[r.branch_id] || r.branch_id || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${r.is_active !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'}`}>
                      {r.is_active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  {isSuperAdmin && (
                    <td className="px-4 py-3 space-x-2">
                      <button onClick={() => resetPassword(r.id)} className="text-xs text-[#659A9B] font-semibold inline-flex items-center gap-1"><KeyRound className="w-3 h-3" /> Reset PW</button>
                      {r.is_active !== false && (
                        <button onClick={() => deactivate(r.id)} className="text-xs text-rose-600 font-semibold inline-flex items-center gap-1"><UserX className="w-3 h-3" /> Deactivate</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={isSuperAdmin ? 6 : 5} className="px-4 py-8 text-center text-gray-500">No staff users</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <StaffUserModal
        open={modalOpen}
        branches={branches}
        onClose={() => setModalOpen(false)}
        onSaved={() => { setModalOpen(false); setToast({ kind: 'ok', text: 'Staff user created' }); fetchRows(); }}
      />
    </div>
  );
}
